<template>
  <div class="card create-report my-10">
    <puls-card-header
      v-if="organizationName && featureName && !loading"
      :title="title"
    />
    <div class="card-body d-flex align-items-center justify-content-center">
      <div v-if="loading" class="loading-row mt-3 mb-3">
        <div class="d-flex justify-content-center m-3">
          <div class="spinner-border text-primary"></div>
        </div>
      </div>
      <div v-else>
        <div
          v-if="!organizationName"
          class="invalid-link justify-content-center h-100"
        >
          <p>
            {{ ft(featureName, 'alerts.notValidLink', $t) }}
          </p>
        </div>
        <div
          v-if="error"
          class="error-row mb-5 d-flex align-items-center justify-content-center"
        >
          <div class="d-flex align-items-center justify-content-center">
            <i class="bi bi-exclamation-octagon-fill fs-3 text-danger mx-3"></i>

            <span class="text-danger"> {{ error }}</span>
          </div>
        </div>
        <div v-if="!error && organizationName">
          <CreateReportForm
            v-if="reportingState === 'create'"
            :initial-reporting-channel="selectedChannel"
            :initial-form-data="formData"
            :organization-full-name="organizationFullName"
            :on-change="onChangeReportForm"
            :organization-links="organizationLinks"
            :feature-name="featureName"
          />
          <CreateReportConfirm
            v-if="reportingState === 'confirm'"
            :organization-full-name="organizationFullName"
            :selected-channel="selectedChannel"
            :message="formData.message"
            :email="formData.email"
            :error="sendReportError"
            :feature-name="featureName"
          />
          <CreateReportResponse
            v-if="reportingState === 'sent'"
            :selected-channel="selectedChannel"
            :email="formData.email"
            :organization-name="organizationName"
            :organization-full-name="organizationFullName"
            :case-name="response.caseName"
            :password="response.password"
            :locale="locale"
            :feature-name="featureName"
          />
        </div>
      </div>
    </div>
    <b-card-footer
      v-if="
        (reportingState === 'create' || reportingState === 'confirm') &&
        !loading &&
        !error
      "
      class="d-flex justify-content-between"
    >
      <div>
        <button
          v-if="reportingState === 'confirm'"
          class="btn btn-round btn-secondary"
          @click="goBack"
        >
          {{ backButton.label }}
        </button>
      </div>
      <div>
        <b-button
          v-if="reportingState === 'create'"
          variant="primary"
          class="btn btn-round"
          :disabled="nextButton.disabled"
          @click="nextButton.action"
        >
          {{ nextButton.label }}
        </b-button>
        <b-button
          v-if="reportingState === 'confirm'"
          variant="primary"
          class="btn btn-round"
          @click="nextButton.action"
        >
          {{ nextButton.label }}
        </b-button>
      </div>
    </b-card-footer>
  </div>
</template>

<script>
import LocalizableComponent from './LocalizableComponent.vue'
import ReporterService from '../services/ReporterService'
import PublicService from '../services/PublicService'
import CreateReportResponse from './CreateReportResponse.vue'
import CreateReportForm from './CreateReportForm.vue'
import CreateReportConfirm from './CreateReportConfirm.vue'

export default {
  name: 'CreateReport',
  components: {
    CreateReportResponse,
    CreateReportForm,
    CreateReportConfirm
  },
  extends: LocalizableComponent,
  data() {
    return {
      loading: false,
      error: null,
      sendReportError: null,
      sent: false,
      organizationName: null,
      reportingState: 'invalidlink',
      response: null,
      organizationFullName: null,
      validForm: false,
      formData: {},
      selectedChannel: 'digital-report',
      organizationLinks: null
    }
  },
  computed: {
    locale() {
      return this.$i18n.locale
    },
    featureName() {
      const featureTypes = {
        h: 'harassment',
        w: 'whistleblower'
      }

      return featureTypes[this.$route.query.f] || featureTypes.w
    },
    nextButton() {
      let label

      if (
        this.reportingState === 'confirm' &&
        this.selectedChannel === 'digital-report'
      ) {
        label = this.ft(this.featureName, 'buttons.send', this.$t)
      } else {
        // create
        label = this.ft(this.featureName, 'buttons.continue', this.$t)
      }
      return {
        label,
        action: this.goNext,
        disabled: !this.validForm
      }
    },
    backButton() {
      const label = this.ft(this.featureName, 'buttons.back', this.$t)
      return { label }
    },

    goNext() {
      return this.reportingState === 'create'
        ? this.confirmReport
        : this.sendReport
    },
    isHarassmentReport() {
      return this.featureName && this.featureName === 'harassment'
    },
    title() {
      let title = this.ft(this.featureName, 'title', this.$t, {
        organizationTitle: this.organizationFullName
      })
      if (this.reportingState === 'sent') {
        if (this.selectedChannel === 'digital-report') {
          title = this.ft(this.featureName, 'received.title', this.$t, {
            organizationTitle: this.organizationFullName
          })
        } else {
          title = this.$t('whistleblower.received.thankyouTitle')
        }
      }

      return title
    }
  },
  watch: {
    reportingState(newState, oldState) {
      const fromConfirmToCreate =
        newState === 'create' && oldState === 'confirm'

      if (fromConfirmToCreate && this.selectedChannel !== 'digital-report') {
        delete this.formData.message
      }
      if (this.reportingState === 'create') {
        this.sendReportError = null
      }
    }
  },
  mounted() {
    this.error = null
    this.sendReportError = null
    if (this.$route.query.o) {
      this.organizationName = this.$route.query.o
      this.reportingState = 'create'
      this.loadPublicReportInfo()
    } else {
      this.reportingState = 'invalidlink'
    }
  },
  methods: {
    onChangeReportForm({ validForm, formData, selectedChannel }) {
      this.validForm = validForm
      this.formData = formData
      this.selectedChannel = selectedChannel
    },
    loadPublicReportInfo() {
      this.loading = true
      this.error = null
      return PublicService.getWhistleblowerInfo(this.organizationName)
        .then((organizationDetails) => {
          const features = Object.keys(organizationDetails)
          features.forEach((featureName) => {
            if (featureName === this.featureName) {
              const f = organizationDetails[featureName]
              if (f.enabled) {
                this.organizationFullName = f.organizationFullName
                  ? f.organizationFullName
                  : this.organizationName

                if (f.links) {
                  this.organizationLinks = f.links
                }
              } else {
                this.error = this.ft(
                  this.featureName,
                  'messages.error.organizationNotFound',
                  this.$t
                )
              }
            }
          })
        })
        .catch((error) => {
          const errorMessage =
            error.response && error.response.status
              ? this.ft(
                  this.featureName,
                  'messages.error.organizationNotFound',
                  this.$t
                )
              : error
          this.error = errorMessage
        })
        .finally(() => {
          this.loading = false
        })
    },
    confirmReport() {
      this.reportingState = 'confirm'
    },
    goBack() {
      this.reportingState = 'create'
    },
    sendReport() {
      this.loading = true
      this.sendReportError = null
      this.reportingState = 'sending'
      const report = {
        organizationName: this.organizationName,
        body: {
          message: this.formData.message,
          reportingChannel: this.selectedChannel,
          featureName: this.featureName
        }
      }
      if (this.formData.email) {
        report.body.email = this.formData.email
      }

      report.body.locale = this.locale
      return ReporterService.createReport(report)
        .then((response) => {
          this.reportingState = 'sent'
          this.response = response
        })
        .catch((error) => {
          this.reportingState = 'confirm'
          const errorMessage =
            error.response && error.response.status
              ? this.ft(
                  this.featureName,
                  'messages.error.failedToSendReport',
                  this.$t
                )
              : error
          this.sendReportError = errorMessage
        })
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style scoped>
.create-report {
  min-height: 30vh;
}
</style>
